import { useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "@/lib/api";

/**
 * useRealtime — subscribes to the server's SSE stream (/api/events/stream) and
 * dispatches events to a handler. Automatically reconnects with exponential backoff.
 *
 * The stream is authenticated via `?token=` query param because EventSource can't
 * set custom Authorization headers.
 */
export function useRealtime(onEvent, enabled = true) {
  const esRef = useRef(null);
  const retryRef = useRef(0);
  const stoppedRef = useRef(false);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    stoppedRef.current = false;

    const connect = () => {
      if (stoppedRef.current) return;
      const token = localStorage.getItem("cq_token");
      if (!token) return;
      const url = `${API}/events/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;
      es.onopen = () => { retryRef.current = 0; };
      es.onmessage = (msg) => {
        try {
          const evt = JSON.parse(msg.data);
          handlerRef.current?.(evt);
        } catch { /* ignore parse errors */ }
      };
      es.onerror = () => {
        try { es.close(); } catch { /* ignore */ }
        esRef.current = null;
        if (stoppedRef.current) return;
        // exponential backoff, cap 30s
        const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current));
        retryRef.current += 1;
        setTimeout(connect, delay);
      };
    };
    connect();
    return () => {
      stoppedRef.current = true;
      try { esRef.current?.close(); } catch { /* ignore */ }
      esRef.current = null;
    };
  }, [enabled]);
}

/**
 * useNotifications — loads persisted notifications, subscribes to live events,
 * exposes { items, unread, markRead, markAllRead, refresh }.
 */
export function useNotifications(enabled = true) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 50 } });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (enabled) refresh(); }, [enabled, refresh]);

  useRealtime((evt) => {
    if (!evt) return;
    if (evt.type === "notification" && evt.data) {
      setItems((prev) => [evt.data, ...prev].slice(0, 50));
      setUnread((u) => u + 1);
    } else if (evt.type === "unread_count" && evt.data) {
      setUnread(evt.data.unread_count || 0);
    } else if (evt.type === "hello" && evt.data) {
      setUnread(evt.data.unread_count || 0);
    }
  }, enabled);

  const markRead = useCallback(async (ids) => {
    await api.post("/notifications/read", { ids });
    setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n)));
    setUnread((u) => Math.max(0, u - ids.length));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.post("/notifications/read", { all: true });
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
  }, []);

  return { items, unread, markRead, markAllRead, refresh };
}
